import './AmcStackedBarChart.scss';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import { abbreviateNumber } from '@src/utils/data';
import {
  CARTESIAN_GRID_STROKE,
  CARTESIAN_GRID_STROKE_WIDTH
} from '@Components/Charts/ChartConstants';
import { ChartTooltip, PayloadItem } from '@Components/Charts/ChartTooltip/ChartTooltip';

const CHART_MARGIN = { top: 20, right: 30, left: 0, bottom: 5 };
const Y_AXIS_WIDTH = 48;
const Y_AXIS_TICKS = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

// abbreviateNumber only rounds values >= 1000 (to the k/m/g/t tier); below that it passes the
// raw number through unrounded, which can be a long floating-point tail.
const formatValue = (value: number): string => {
  const abbreviated = abbreviateNumber(value);
  return typeof abbreviated === 'number' ? abbreviated.toFixed(2) : abbreviated;
};

interface AmcStackedBarChartProps {
  data: Record<string, unknown>[];
  // Raw data keys (e.g. 'ntbCustomers') — used to look up each bar's color and, via the tooltip,
  // the raw (non-percent) value for the series being hovered.
  dataKeys: string[];
  // The *Percent-suffixed keys actually plotted — the bars render as a 100%-stacked share of the
  // two series for that month, while the tooltip shows the underlying raw counts.
  percentKeys: string[];
  fill: Record<string, string>;
  dataLabel: Record<string, string>;
}

interface AmcStackedBarChartTooltipProps {
  active?: boolean;
  payload?: { dataKey: string; payload: Record<string, unknown> }[];
  label?: string;
  dataKeys: string[];
  percentKeys: string[];
  fill: Record<string, string>;
  dataLabel: Record<string, string>;
}

const AmcStackedBarChartTooltip = ({
  active,
  payload,
  label,
  dataKeys,
  percentKeys,
  fill,
  dataLabel
}: AmcStackedBarChartTooltipProps) => {
  if (!active || !payload || payload.length === 0) return null;
  const items: PayloadItem[] = percentKeys.map((percentKey, index) => {
    const rawKey = dataKeys[index];
    return {
      label: dataLabel[percentKey],
      value: formatValue(Number(payload[0].payload[rawKey])),
      color: fill[rawKey]
    };
  });
  return <ChartTooltip headerLabel={label ?? ''} items={items} />;
};

interface AmcStackedBarChartLegendProps {
  dataKeys: string[];
  percentKeys: string[];
  fill: Record<string, string>;
  dataLabel: Record<string, string>;
}

const AmcStackedBarChartLegend = ({
  dataKeys,
  percentKeys,
  fill,
  dataLabel
}: AmcStackedBarChartLegendProps) => (
  <div className='amc-stacked-bar-chart-legend'>
    {percentKeys.map((percentKey, index) => (
      <div className='legend-item' key={percentKey}>
        <span className='legend-swatch' style={{ backgroundColor: fill[dataKeys[index]] }} />
        {dataLabel[percentKey]}
      </div>
    ))}
  </div>
);

export const AmcStackedBarChart = ({
  data,
  dataKeys,
  percentKeys,
  fill,
  dataLabel
}: AmcStackedBarChartProps) => (
  <div className='amc-stacked-bar-chart'>
    <AmcStackedBarChartLegend
      dataKeys={dataKeys}
      percentKeys={percentKeys}
      fill={fill}
      dataLabel={dataLabel}
    />
    <div className='amc-stacked-bar-chart-plot'>
      <AutoSizer
        renderProp={({ width = 0, height = 0 }) => (
          <BarChart data={data} width={width} height={height} margin={CHART_MARGIN}>
            <CartesianGrid
              stroke={CARTESIAN_GRID_STROKE}
              strokeWidth={CARTESIAN_GRID_STROKE_WIDTH}
              vertical={false}
            />
            <XAxis dataKey='month' axisLine={false} tickLine={false} tickMargin={5} />
            <YAxis
              domain={[0, 100]}
              ticks={Y_AXIS_TICKS}
              type='number'
              unit='%'
              axisLine={false}
              tickLine={false}
              width={Y_AXIS_WIDTH}
            />
            <Tooltip
              isAnimationActive={false}
              cursor={{ fill: 'transparent' }}
              content={
                <AmcStackedBarChartTooltip
                  dataKeys={dataKeys}
                  percentKeys={percentKeys}
                  fill={fill}
                  dataLabel={dataLabel}
                />
              }
            />
            {percentKeys.map((percentKey, index) => (
              <Bar
                key={percentKey}
                dataKey={percentKey}
                fill={fill[dataKeys[index]]}
                stackId='a'
                isAnimationActive={false}
              />
            ))}
          </BarChart>
        )}
      />
    </div>
  </div>
);
